import React, { useEffect } from 'react';
import { Field, Formik } from 'formik';
import * as Yup from 'yup';
import { connect } from 'react-redux';

import * as actions from '../../../Store/Actions';
import {
  AuthBrand,
  AuthCard,
  AuthDescription,
  AuthFieldGrid,
  AuthKicker,
  AuthNotice,
  AuthPage,
  AuthRoleChoice,
  AuthRoleIcon,
  AuthRoleSelector,
  AuthRoleSelectorLabel,
  AuthRoleState,
  AuthRoleText,
  AuthSignInLink,
  AuthSignInPanel,
  AuthTitle,
} from '../../../Components/Auth/AuthLayout';
import { StyledForm } from '../../../Hoc/elements';
import Input from '../../../Components/UI/Forms/Inputs/Input';
import Button from '../../../Components/UI/Forms/Button/Button';
import Message from '../../../Components/UI/Messages/Message';
import TermsAcceptance from '../../../Components/Trust/TermsAcceptance';
import { MARKETPLACE_TERMS_VERSION, REVIEW_POLICY_VERSION } from '../../../Config/marketplace';

const SignUpSchema = Yup.object().shape({
  firstName: Yup.string().trim().required('Votre prénom est requis.').min(2, 'Minimum 2 caractères.').max(40, 'Maximum 40 caractères.'),
  lastName: Yup.string().trim().required('Votre nom est requis.').min(2, 'Minimum 2 caractères.').max(50, 'Maximum 50 caractères.'),
  email: Yup.string().email('Adresse e-mail invalide.').required('Votre adresse e-mail est requise.'),
  password: Yup.string().required('Votre mot de passe est requis.').min(8, 'Minimum 8 caractères.'),
  confirmPassword: Yup.string().oneOf([Yup.ref('password'), null], 'Les mots de passe ne correspondent pas.').required('Confirmez votre mot de passe.'),
  acceptedTerms: Yup.boolean().oneOf([true], 'Vous devez accepter les conditions pour créer un compte.'),
});

const SignUp = ({ cleanup, error, loading, signUp }) => {
  useEffect(() => {
    cleanup();
  }, [cleanup]);

  return (
    <AuthPage>
      <Formik
        initialValues={{ firstName: '', lastName: '', email: '', password: '', confirmPassword: '', acceptedTerms: false }}
        validationSchema={SignUpSchema}
        validateOnMount
        onSubmit={(values) => signUp({
          ...values,
          termsVersion: MARKETPLACE_TERMS_VERSION,
          reviewPolicyVersion: REVIEW_POLICY_VERSION,
        })}
      >
        {({ errors, isSubmitting, isValid, setFieldTouched, setFieldValue, touched, values }) => (
          <AuthCard>
            <AuthBrand subtitle="Inscription sécurisée" />

            <AuthRoleSelectorLabel>Quel compte souhaitez-vous créer ?</AuthRoleSelectorLabel>
            <AuthRoleSelector aria-label="Choisir le type de compte">
              <AuthRoleChoice to="/signup" data-active="true" aria-current="page">
                <AuthRoleIcon data-active="true" aria-hidden="true">C</AuthRoleIcon>
                <AuthRoleText>
                  <strong>Compte client</strong>
                  <span>Je publie mes demandes</span>
                </AuthRoleText>
                <AuthRoleState data-active="true">Sélectionné</AuthRoleState>
              </AuthRoleChoice>

              <AuthRoleChoice to="/registerartisan" data-active="false">
                <AuthRoleIcon data-active="false" aria-hidden="true">A</AuthRoleIcon>
                <AuthRoleText>
                  <strong>Compte artisan</strong>
                  <span>Je réponds aux demandes</span>
                </AuthRoleText>
                <AuthRoleState data-active="false">Choisir</AuthRoleState>
              </AuthRoleChoice>
            </AuthRoleSelector>

            <AuthKicker>Inscription client · Étape 1 sur 2</AuthKicker>
            <AuthTitle>Créer mon compte client</AuthTitle>
            <AuthDescription>
              Ce compte est destiné aux particuliers et aux professionnels qui souhaitent publier un besoin, recevoir des propositions et suivre leurs travaux.
            </AuthDescription>

            <StyledForm noValidate>
              <AuthFieldGrid>
                <Field type="text" name="firstName" label="Prénom" placeholder="Votre prénom" autoComplete="given-name" component={Input} />
                <Field type="text" name="lastName" label="Nom" placeholder="Votre nom" autoComplete="family-name" component={Input} />
              </AuthFieldGrid>
              <Field type="email" name="email" label="Adresse e-mail" placeholder="vous@exemple.fr" autoComplete="email" component={Input} />
              <AuthFieldGrid>
                <Field type="password" name="password" label="Mot de passe" placeholder="8 caractères minimum" autoComplete="new-password" component={Input} />
                <Field type="password" name="confirmPassword" label="Confirmation" placeholder="Retapez le mot de passe" autoComplete="new-password" component={Input} />
              </AuthFieldGrid>
              <AuthNotice>
                <strong>Vous créez bien un compte client</strong>
                Après l’inscription, vous pourrez publier une demande et comparer les propositions des artisans.
              </AuthNotice>
              <TermsAcceptance
                checked={values.acceptedTerms}
                onChange={(event) => {
                  setFieldValue('acceptedTerms', event.target.checked);
                  setFieldTouched('acceptedTerms', true, false);
                }}
                error={touched.acceptedTerms && !values.acceptedTerms ? errors.acceptedTerms : ''}
              />
              <Button disabled={!isValid || isSubmitting} loading={loading ? 'Création du compte client…' : null} type="submit">
                Créer mon compte client
              </Button>

              <AuthSignInPanel>
                <div>
                  <strong>Vous avez déjà un compte ?</strong>
                  <span>Connectez-vous pour retrouver vos demandes, messages et projets.</span>
                </div>
                <AuthSignInLink to="/login">Se connecter</AuthSignInLink>
              </AuthSignInPanel>

              <Message error show={Boolean(error)}>{error}</Message>
            </StyledForm>
          </AuthCard>
        )}
      </Formik>
    </AuthPage>
  );
};

const mapStateToProps = ({ auth }) => ({ loading: auth.loading, error: auth.error });
const mapDispatchToProps = { signUp: actions.signUp, cleanup: actions.cleanMessage };

export default connect(mapStateToProps, mapDispatchToProps)(SignUp);
